import React, { useEffect, useState } from "react";
import { useSiteStore } from "../../stores/Assets/site";
import { useMenuStore } from "../../stores/Assets/menu";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, Badge } from "reactstrap";
import EditMenu from "../Menus/Components/EditMenu";
import EditBranchMenu from "./components/EditBranchMenu";
import {
    Edit,
    Plus,
    Trash2,
    Utensils,
    MoreVertical,
    LayoutGrid,
    Box,
    ChevronDown,
    ChevronUp,
    Search,
    Filter,
    ArrowRight,
    GripVertical,
    X,
    Check,
    Zap,
    ChevronRight,
    Copy,
    Loader2,
    Settings2,
} from "lucide-react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useUsageStore } from "../../stores";
import { PlanUpgradeModal } from "../../Components/Common/PlanUpgradeModal";

const THEME_COLOR = '#c59253';

const MyMenu = () => {
    const { t } = useTranslation();
    const { sites, createSite, setSites, deleteSite } = useSiteStore();
    const { getSitesWithFullMenus, saveSiteMenuStructure } = useMenuStore();
    const [LoadingData, setLoadingData] = useState(false);
    const [showEditMenuModal, setShowEditMenuModal] = useState(false);
    const [selectedMenu, setSelectedMenu] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    const { fetchUsage, getLimitInfo } = useUsageStore();
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const siteLimit = getLimitInfo("sites");
    const isLimitReached = siteLimit?.isReached;

    // Modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [branchToAction, setBranchToAction] = useState(null);

    const [editingSiteId, setEditingSiteId] = useState(null);

    const [isCreating, setIsCreating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Accordion State: Track which sites are expanded
    const [expandedSites, setExpandedSites] = useState([]);
    const [siteMenus, setSiteMenus] = useState({}); // { siteId: [menus] }

    //instead of calling menu groups we call the endpoint for sites only
    //then we can only have one site having one menu(multiple sections) 
    //clicking on edit will open the same page but now we are gonna save to the site
    //no need to have new group button we dont create a new menu, instead we call it new branch 
    //clicking on it, will first check older branch if exist and prefill the location, lat long, phone, and keep the name up to change
    //it will ask the user if the branch is on this same location else he can modify, then it will ask do you want to duplicate the menu from that initial branch to the new one
    //then he can go to that view where he can edit

    //by default all the dropdowns are closed except the first on the list
    //now each card represents a branch 

    const fetchAllData = async () => {
        const fullData = await getSitesWithFullMenus();
        if (fullData) {
            const formattedSites = fullData.map(site => ({
                ...site,
                logo: site.image_url
            }));
            setSites(formattedSites);

            const menusMap = {};
            fullData.forEach(site => {
                menusMap[site.site_id] = site.menus;
            });
            setSiteMenus(menusMap);
        }
    };

    useEffect(() => {
        const load = async () => {
            setLoadingData(true);
            await Promise.all([
                fetchAllData(),
                fetchUsage()
            ]);
            setLoadingData(false);
        };
        load();
    }, []);

    useEffect(() => {
        if (sites && sites.length > 0 && expandedSites.length === 0) {
            setExpandedSites([sites[0].site_id]);
        }
    }, [sites]);


    const toggleSite = (siteId) => {
        setExpandedSites(prev =>
            prev.includes(siteId)
                ? prev.filter(id => id !== siteId)
                : [...prev, siteId]
        );
    };

    const toggleEditMenuModal = () => {
        setShowEditMenuModal(!showEditMenuModal);
        if (showEditMenuModal) {
            setSelectedMenu(null);
        }
    };

    const handleEditMenu = (menu) => {
        setSelectedMenu(menu);
        setShowEditMenuModal(true);
    };

    const handleEditBranch = (site) => {
        setEditingSiteId(site.site_id);
    };

    const handleOpenCreateModal = () => {
        if (isLimitReached) {
            setShowUpgradeModal(true);
            return;
        }
        setEditingSiteId('new'); // Use 'new' as a flag for creation
    };

    const openDeleteModal = (site) => {
        setBranchToAction(site);
        setShowDeleteModal(true);
    };

    const confirmDeleteBranch = async () => {
        if (!branchToAction) return;
        setIsDeleting(true);
        try {
            await deleteSite(branchToAction.site_id);
            toast.success(t("Branch deleted successfully"));
            fetchAllData();
            setShowDeleteModal(false);
            setBranchToAction(null);
        } catch (error) {
            console.error(error);
            toast.error(t("Failed to delete branch"));
        } finally {
            setIsDeleting(false);
        }
    };

    const openDuplicateModal = (site) => {
        if (isLimitReached) {
            setShowUpgradeModal(true);
            return;
        }
        setBranchToAction(site);
        setShowDuplicateModal(true);
    };


    const confirmDuplicateBranch = async () => {
        if (!branchToAction) return;
        const site = branchToAction;

        setIsCreating(true);
        try {
            // 1. Create the site first
            const sitePayload = new FormData();
            sitePayload.append('name', `${site.name} (Copy)`);
            sitePayload.append('name_ar', site.name_ar ? `${site.name_ar} (نسخة)` : '');
            sitePayload.append('address', site.address || '');
            sitePayload.append('address_ar', site.address_ar || '');
            sitePayload.append('phone', site.phone || '');
            // Image URL duplication might depend on how backend handles URL vs File
            if (site.image_url) {
                sitePayload.append('image_url', site.image_url);
            }

            const newSite = await createSite(sitePayload);
            if (!newSite) throw new Error("Failed to create site");

            // 2. Fetch original site menu and save it to the new one
            const menuPayload = {
                menus: site.menus.map(menu => ({
                    menu_id: 0,
                    name: menu.name,
                    name_ar: menu.name_ar,
                    description: menu.description,
                    description_ar: menu.description_ar,
                    image_url: menu.image_url,
                    items: (menu.menu_items || []).map(item => ({
                        menu_item_id: 0,
                        title: item.title,
                        title_ar: item.title_ar,
                        description: item.description,
                        description_ar: item.description_ar,
                        price: item.price,
                        image_url: item.image_url || (item.item_images?.[0]?.image_url),
                        options: (item.menuItem_options || []).map(optConn => {
                            const opt = optConn.menu_item_option;
                            return {
                                name: opt.name,
                                name_ar: opt.name_ar,
                                choices: (opt.choices || []).map(choice => ({
                                    name: choice.name,
                                    name_ar: choice.name_ar,
                                    price: choice.price
                                }))
                            };
                        })
                    }))
                })),
                deleted_menu_ids: [],
                deleted_item_ids: []
            };

            await saveSiteMenuStructure(newSite.site_id, menuPayload);
            toast.success(t("Branch duplicated successfully with menu and options!"));
            fetchAllData();
            setShowDuplicateModal(false);
            setBranchToAction(null);
        } catch (error) {
            console.error(error);
            toast.error(t("Failed to duplicate branch"));
        } finally {
            setIsCreating(false);
        }
    };


    const filteredSites = sites?.filter(site =>
        site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        site.address?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: "spring", stiffness: 300, damping: 24 }
        }
    };

    return (
        <div className="bg-light min-vh-100 font-sans">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
                
                body {
                    font-family: 'Outfit', sans-serif !important;
                }
                .font-sans { font-family: 'Outfit', sans-serif !important; }
                .nav-tabs-custom .nav-item .nav-link.active {
                    color: ${THEME_COLOR};
                }
                .custom-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scroll::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scroll::-webkit-scrollbar-thumb {
                    background-color: rgba(244, 241, 241, 0.1);
                    border-radius: 20px;
                }
                .hover-scale {
                    transition: transform 0.2s;
                }
                .hover-scale:hover {
                    transform: scale(1.02);
                }
                .glass-card {
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }
                .menu-card {
                    transition: all 0.2s ease;
                }
                .menu-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
                    border-color: ${THEME_COLOR} !important;
                }
                .group-header {
                    background: #fff;
                    transition: background-color 0.2s;
                    cursor: pointer;
                }
                .group-header:hover {
                    background-color: #f8f9fa;
                }
                .dnd-list {
                    min-height: 400px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    border: 1px dashed #dee2e6;
                }
                .dnd-item {
                    user-select: none;
                    background: white;
                    border: 1px solid #eee;
                }
                .dnd-item.dragging {
                    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
                    border-color: ${THEME_COLOR} !important;
                    z-index: 1000;
                }
                .hover-bg-danger:hover { background-color: #dc3545 !important; border-color: #dc3545 !important; }
                .text-hover-white:hover * { color: white !important; }
                .transition-all { transition: all 0.2s ease-in-out; }
                .bg-soft-primary { background-color: rgba(197, 146, 83, 0.1); }
            `}</style>

            <div className="container-fluid px-4 py-4">
                {/* Header Section */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5 gap-3">
                    <div>
                        <h1 className="fw-bold mb-1" style={{ color: '#2b2b2b' }}>{t("My Menus")}</h1>
                        <p className="text-muted mb-0">{t("Manage my Menus")}</p>
                    </div>

                    <div className="d-flex gap-3 align-items-center">
                        <div className="position-relative d-none d-md-block" style={{ width: '300px' }}>
                            <Search size={16} className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                            <input
                                type="text"
                                className="form-control rounded-pill border-0 shadow-sm ps-5 bg-white small"
                                placeholder={t("Search branches or locations...")}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn text-white rounded-pill px-4 py-2 d-flex align-items-center gap-2 shadow-sm fw-bold border-0 small"
                            style={{ backgroundColor: THEME_COLOR }}
                            onClick={handleOpenCreateModal}
                        >
                            <Plus size={16} strokeWidth={2.5} />
                            <span>{t("New Menu")}</span>
                        </motion.button>
                    </div>
                </div>

                {LoadingData ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <div className="spinner-border text-[#C59253]" role="status">
                            <div className="visually-hidden">Loading...</div>
                        </div>
                    </div>
                ) : (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="d-flex flex-column gap-3"
                    >
                        <AnimatePresence>
                            {filteredSites && filteredSites.length > 0 ? (
                                filteredSites.map((site) => {
                                    const isExpanded = expandedSites.includes(site.site_id);
                                    const branches = siteMenus[site.site_id] || [];

                                    return (
                                        <motion.div
                                            key={site.site_id}
                                            variants={itemVariants}
                                            className="bg-white rounded-3 shadow-sm border overflow-hidden"
                                        >
                                            {/* Collapsible Header */}
                                            <div
                                                className="group-header px-4 py-3 d-flex justify-content-between align-items-center"
                                                onClick={() => toggleSite(site.site_id)}
                                            >
                                                <div className="d-flex align-items-center gap-3">
                                                    <div
                                                        className={`transition-transform d-flex align-items-center justify-content-center text-muted`}
                                                        style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
                                                    >
                                                        <ChevronDown size={20} />
                                                    </div>
                                                    <div className="rounded-3 d-flex align-items-center justify-content-center bg-light"
                                                        style={{ width: '36px', height: '36px', overflow: 'hidden' }}>
                                                        {site.image_url ? (
                                                            <img src={site.image_url} alt="" className="w-100 h-100 object-fit-cover" />
                                                        ) : (
                                                            <LayoutGrid size={18} style={{ color: THEME_COLOR }} />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-bold">{site.name}</h6>
                                                        {!isExpanded && (
                                                            <span className="x-small text-muted">{branches.length} {t("Sections")}</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-2">
                                                    {isExpanded && (
                                                        <span className="badge bg-light text-muted fw-normal me-2 border">
                                                            {branches.length} {t("Sections")}
                                                        </span>
                                                    )}
                                                    <button
                                                        className="btn btn-light btn-sm rounded-circle p-1 hover-bg-light"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditBranch(site);
                                                        }}
                                                        title={t("Edit Menu")}
                                                    >
                                                        <Edit size={14} className="text-muted" />
                                                    </button>
                                                    <button
                                                        className="btn btn-light btn-sm rounded-circle p-1 hover-bg-light"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openDuplicateModal(site);
                                                        }}
                                                        title={t("Duplicate Menu")}
                                                    >
                                                        <Copy size={14} className="text-muted" />
                                                    </button>
                                                    <button
                                                        className="btn btn-light btn-sm rounded-circle p-1 hover-bg-danger text-hover-white"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openDeleteModal(site);
                                                        }}
                                                        title={t("Delete Menu")}
                                                    >
                                                        <Trash2 size={14} className="text-muted" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Expandable Content */}
                                            <AnimatePresence initial={false}>
                                                {isExpanded && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: "auto", opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{ duration: 0.3, ease: "easeInOut" }}
                                                    >
                                                        <div className="p-4 bg-light bg-opacity-10 border-top">
                                                            {branches && branches.length > 0 ? (
                                                                <div className="row g-3">
                                                                    {branches.map((menu) => (
                                                                        <div className="col-12 col-md-6 col-lg-4 col-xl-3" key={menu.menu_id}>
                                                                            <motion.div
                                                                                whileHover={{ y: -2 }}
                                                                                className="card h-100 border border-light shadow-sm menu-card overflow-hidden rounded-3 bg-white"
                                                                            >
                                                                                <div className="d-flex position-relative h-100">
                                                                                    {/* Left: Image / Icon - Compact */}
                                                                                    <div className="flex-shrink-0 d-flex align-items-center justify-content-center border-end" style={{ width: '60px', backgroundColor: '#f8f9fa' }}>
                                                                                        {menu.image_url ? (
                                                                                            <img
                                                                                                src={menu.image_url}
                                                                                                alt={menu.name}
                                                                                                className="w-100 h-100 object-fit-cover"
                                                                                            />
                                                                                        ) : (
                                                                                            <Utensils size={20} className="text-muted opacity-50" />
                                                                                        )}
                                                                                    </div>

                                                                                    {/* Right: Content */}
                                                                                    <div className="flex-grow-1 p-2 px-3 d-flex flex-column justify-content-between">
                                                                                        <div>
                                                                                            <div className="d-flex justify-content-between align-items-start mb-1">
                                                                                                <h6 className="fw-bold text-dark text-truncate mb-0 small" style={{ maxWidth: '85%' }} title={menu.name}>
                                                                                                    {menu.name}
                                                                                                </h6>
                                                                                                <button
                                                                                                    className="btn btn-link btn-sm p-0 text-muted"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handleEditMenu(menu);
                                                                                                    }}
                                                                                                >
                                                                                                    <Edit size={12} />
                                                                                                </button>
                                                                                            </div>
                                                                                            <p className="text-muted x-small text-truncate mb-2" style={{ maxWidth: '180px', fontSize: '11px' }}>
                                                                                                {menu.description || t("No description")}
                                                                                            </p>
                                                                                        </div>

                                                                                        {/* Menu Items Badges */}
                                                                                        <div className="mb-2">
                                                                                            {menu.menu_items && menu.menu_items.length > 0 ? (
                                                                                                <div className="d-flex flex-wrap gap-1">
                                                                                                    {menu.menu_items.slice(0, 3).map((item, idx) => (
                                                                                                        <span key={idx} className="badge bg-light text-secondary border fw-normal" style={{ fontSize: '9px', padding: '2px 5px' }}>
                                                                                                            {item.title}
                                                                                                        </span>
                                                                                                    ))}
                                                                                                    {menu._count?.menu_items > 3 && (
                                                                                                        <span className="badge bg-soft-primary text-primary fw-normal" style={{ fontSize: '9px', padding: '2px 5px' }}>
                                                                                                            +{menu._count.menu_items - 3}
                                                                                                        </span>
                                                                                                    )}
                                                                                                </div>
                                                                                            ) : (
                                                                                                <span className="text-muted x-small fst-italic opacity-50">{t("Empty section")}</span>
                                                                                            )}
                                                                                        </div>

                                                                                        <div className="d-flex align-items-center justify-content-between border-top border-light pt-2 mt-auto">
                                                                                            <span className="badge bg-light text-muted border text-dark x-small fw-normal" style={{ fontSize: '10px' }}>
                                                                                                {t("ID")}: {menu.menu_id}
                                                                                            </span>
                                                                                            {menu.updated_at && (
                                                                                                <span className="x-small text-muted" style={{ fontSize: '9px' }}>
                                                                                                    {new Date(menu.updated_at).toLocaleDateString()}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </motion.div>
                                                                        </div>
                                                                    ))}

                                                                    {/* Edit Full Site Action */}
                                                                    <div className="col-12 col-md-6 col-lg-4 col-xl-3">
                                                                        <motion.div
                                                                            whileHover={{ scale: 1.02 }}
                                                                            whileTap={{ scale: 0.98 }}
                                                                            className="card h-100 border border-dashed rounded-3 bg-white d-flex align-items-center justify-content-center cursor-pointer shadow-none"
                                                                            style={{ minHeight: '80px', borderColor: '#e0e0e0' }}
                                                                            onClick={() => handleEditBranch(site)}
                                                                        >
                                                                            <div className="text-center">
                                                                                <Settings2 size={18} className="text-muted mb-1" />
                                                                                <span className="d-block small text-muted fw-bold">{t("Manage Sections")}</span>
                                                                            </div>
                                                                        </motion.div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="text-center py-4">
                                                                    <div className="bg-white p-3 rounded-circle shadow-sm d-inline-block mb-2">
                                                                        <Utensils size={24} className="text-muted opacity-50" />
                                                                    </div>
                                                                    <h6 className="text-muted fw-bold small">{t("No sections yet")}</h6>
                                                                    <p className="text-muted x-small mb-3">{t("Start building your menu for this branch")}</p>
                                                                    <button
                                                                        className="btn btn-outline-primary btn-sm rounded-pill px-3 py-1 x-small"
                                                                        onClick={() => handleEditBranch(site)}
                                                                    >
                                                                        {t("Setup Menu")}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </motion.div>
                                    );
                                })
                            ) : null}

                            {/* New Branch Inline Card */}
                            <motion.div
                                variants={itemVariants}
                                onClick={handleOpenCreateModal}
                                className="bg-white rounded-3 shadow-sm border border-dashed p-4 d-flex align-items-center justify-content-center cursor-pointer hover-bg-light transition-all"
                                style={{ minHeight: '120px', borderStyle: 'dashed', borderWidth: '2px' }}
                            >
                                <div className="text-center">
                                    <div className="bg-primary bg-opacity-10 p-2 rounded-circle d-inline-block mb-2">
                                        <Plus size={24} className="text-primary" />
                                    </div>
                                    <h6 className="fw-bold text-dark mb-0">{t("Add Another Menu")}</h6>
                                    <p className="text-muted x-small mb-0">{t("Create a new branch and menu.")}</p>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </motion.div>
                )}
            </div>

            {/* Create Branch Modal - Multi Step*/}

            {/* Edit Menu Modal */}
            {selectedMenu && (
                <EditMenu
                    siteList={sites}
                    rowData={selectedMenu}
                    showEditMenuModal={showEditMenuModal}
                    toggleEditMenuModal={toggleEditMenuModal}
                />
            )}


            {/* Edit Branch Full Screen Component */}
            {editingSiteId && (
                <EditBranchMenu
                    siteId={editingSiteId}
                    onClose={() => {
                        setEditingSiteId(null);
                        fetchAllData(); // Refresh all data on close
                    }}
                />
            )}

            {/* Delete Confirmation Modal */}
            <Modal isOpen={showDeleteModal} toggle={() => setShowDeleteModal(!showDeleteModal)} centered>
                <ModalHeader toggle={() => setShowDeleteModal(!showDeleteModal)}>{t("Delete Menu")}</ModalHeader>
                <ModalBody>
                    <p className="text-muted mb-0">
                        {t("Are you sure you want to delete this menu?")} {branchToAction?.name}
                    </p>
                    <p className="text-danger small mt-2 fw-bold">
                        {t("This action cannot be undone.")}
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>{t("Cancel")}</Button>
                    <Button color="danger" onClick={confirmDeleteBranch} disabled={isDeleting}>
                        {isDeleting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {t("Deleting...")}
                            </>
                        ) : (
                            t("Delete")
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Duplicate Confirmation Modal */}
            <Modal isOpen={showDuplicateModal} toggle={() => setShowDuplicateModal(!showDuplicateModal)} centered>
                <ModalHeader toggle={() => setShowDuplicateModal(!showDuplicateModal)}>{t("Duplicate Branch")}</ModalHeader>
                <ModalBody>
                    <p className="text-muted">
                        {t("Are you sure you want to duplicate this branch and its menu?")}
                    </p>
                    <div className="alert alert-info py-2 small mb-0">
                        <i className="mdi mdi-information me-2"></i>
                        {t("This will create a copy of")} <strong>{branchToAction?.name}</strong> {t("along with all sections and items.")}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setShowDuplicateModal(false)} disabled={isCreating}>{t("Cancel")}</Button>
                    <Button
                        style={{ backgroundColor: THEME_COLOR, borderColor: THEME_COLOR }}
                        className="text-white"
                        onClick={confirmDuplicateBranch}
                        disabled={isCreating}
                    >
                        {isCreating ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {t("Duplicating...")}
                            </>
                        ) : (
                            t("Duplicate")
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            <PlanUpgradeModal
                isOpen={showUpgradeModal}
                toggle={() => setShowUpgradeModal(!showUpgradeModal)}
            />
        </div>
    );
};

export default MyMenu;